import { z } from "zod";
import { DeliveryFormSchema } from "@/types/forms/home/cotationDeliveryForm.form";
import { GetOneExpeditionBoResponseDto } from "@/types/api/api-types";
import { WineColorEnum } from "@/constants/enums/wineColor.enum";
import { parseEnum } from "@/lib/enums.util";
import { i18nMessage } from "@/lib/i18nMessage";

export const ReQuotationBoFormSchema = z
  .object({
    basedOnId: z.string(),
    skipPickup: z.boolean({
      required_error: i18nMessage("boExpeditionForm:expeditionValidation.skipPickupRequired"),
      invalid_type_error: i18nMessage("boExpeditionForm:expeditionValidation.skipPickupRequired"),
    }),
    note: z
      .string({ message: i18nMessage("boExpeditionForm:expeditionValidation.noteRequired") })
      .min(1, { message: i18nMessage("boExpeditionForm:expeditionValidation.noteRequired") }),
    transportDiscountPercentage: z.number().min(0).max(100).optional().default(0),
    overallDiscountPercentage: z.number().min(0).max(100).optional().default(0),
  })
  .and(DeliveryFormSchema);

export type ReQuotationBoFormData = z.infer<typeof ReQuotationBoFormSchema>;

export const QuotationUpdateBoFormSchema = z
  .object({
    trackingUrl: z.string().url().optional(),
    trackingCode: z
      .string()
      .regex(/^\d+$/, {
        message: i18nMessage("boExpeditionForm:expeditionValidation.trackingCodeDigitsOnly"),
      })
      .optional(),
    isDraft: z.boolean().optional(),
    updatePriceParameters: z.boolean().optional(),
  })
  .and(DeliveryFormSchema);

export type QuotationUpdateBoFormData = z.infer<typeof QuotationUpdateBoFormSchema>;

type Props = {
  expedition: GetOneExpeditionBoResponseDto;
};

export const QuotationUpdateBoDefaultValues = ({ expedition }: Props) => {
  const hasDeliveryContent =
    expedition?.quotation?.deliveryContent && expedition?.quotation?.deliveryContent.length > 0;

  const deliveryContent = hasDeliveryContent
    ? expedition.quotation!.deliveryContent!.map((product) => ({
        wineName: product.wineName,
        year: product.year,
        alcoholPercent: product.alcoholPercent,
        volume: product.volume,
        color: parseEnum(WineColorEnum, product.color) ?? WineColorEnum.ROUGE,
        quantity: product.numberOfBottles,
        price: product.unitPrice,
      }))
    : expedition.quotation!.quotationProducts!.map((product) => ({
        wineName: "xxxx",
        year: undefined,
        alcoholPercent: 0,
        volume: product.volume,
        color: WineColorEnum.ROUGE,
        quantity: product.bottleQuantity,
        price: parseFloat((product.value / product.bottleQuantity).toFixed(4)),
      }));

  return {
    company: expedition?.deliveryInfo?.company || undefined,
    lastName: expedition?.deliveryInfo?.lastName || undefined,
    firstName: expedition?.deliveryInfo?.firstName || undefined,
    email: expedition?.deliveryInfo?.email || undefined,
    phone: expedition?.deliveryInfo?.phone || undefined,
    street: expedition?.deliveryInfo?.street || undefined,
    postalCode: expedition?.deliveryInfo?.postalCode || undefined,
    city: expedition?.deliveryInfo?.city || undefined,
    countryCode: expedition?.deliveryInfo?.country || undefined,
    stateOrProvinceCode: expedition?.deliveryInfo?.state || undefined,
    customId: expedition?.quotation?.customId || undefined,
    deliveryContent,
    updatePriceParameters: false,
    woodBoxes:
      expedition?.quotation?.quotationWoodBox?.map((woodBox) => ({
        bottleQuantity: woodBox.bottleQuantity,
        boxQuantity: woodBox.boxQuantity,
        bottleVolume: woodBox.bottleVolume,
      })) || [],
    billingEntityId: expedition.expeditionBillingDetail?.billingEntityId || undefined,
    pickUpLocationId: expedition.pickupInfo?.pickupLocation?.id || undefined,
    comment: expedition?.deliveryInfo?.comment || undefined,
    insurance: expedition?.expeditionOptions?.insurance || false,
    dapOption: expedition?.expeditionOptions?.dapOption || false,
    express: expedition?.expeditionOptions?.express || false,
    reference: expedition?.expeditionBillingDetail?.reference || undefined,
    trackingCode: expedition?.tracking?.trackingCode || undefined,
    trackingUrl: expedition?.tracking?.trackingUrl || undefined,
  };
};
